/**
 * Custom Adventure Request Management Component
 * 
 * Business dashboard interface for reviewing and responding to custom adventure booking requests
 * Features:
 * - Table-based layout for efficient space usage
 * - Accept/Decline/Modify response options with professional UX
 * - Integrated communication tracking
 * - Status filtering and search capabilities
 * - Modern design following Business Design System
 * 
 * @author Wildlife Project Team
 * @version 2.0 - Refactored with DataTable component
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Clock, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Edit3, 
  Heart, 
  AlertTriangle,
  MessageSquare,
  Calendar,
  Users,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/adventureUtils';
import { toast } from 'sonner';

// Import design system tokens
import { getCardClasses } from '@/components/business/design-system/businessDesignTokens';
import { 
  getBusinessBookingRequests, 
  getBookingRequestById, 
  respondToBookingRequest,
  type BookingRequestListItem,
  type BookingRequestDetails
} from '@/utils/businessAppApi';

// Import the new DataTable component
import { DataTable, type TableColumn, type TableAction } from '../shared/DataTable';

interface RequestFilters {
  status?: string;
  dateRange?: { from: Date; to: Date };
  searchTerm?: string;
}

export function CustomAdventureRequestManagement() {
  const [requests, setRequests] = useState<BookingRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<BookingRequestDetails | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Details dialog state
  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    request: BookingRequestDetails | null;
  }>({ open: false, request: null });

  // Response dialog states
  const [responseDialog, setResponseDialog] = useState<{
    open: boolean;
    type: 'accept' | 'decline' | 'modify';
    request: BookingRequestListItem | null;
  }>({ open: false, type: 'accept', request: null });

  const [responseForm, setResponseForm] = useState({
    message: '',
    proposedModifications: {
      dateRange: { startDate: '', endDate: '' },
      pricing: { modifiedPrice: '', reason: '' },
      options: {}
    }
  });

  // Load booking requests
  useEffect(() => {
    loadBookingRequests();
  }, []);

  const loadBookingRequests = async () => {
    try {
      setLoading(true);
      const data = await getBusinessBookingRequests();
      console.log('🔍 [BookingRequests] Loaded requests:', data.requests);
      console.log('🔍 [BookingRequests] First request sample:', data.requests[0]);
      setRequests(data.requests);
    } catch (error) {
      console.error('Error loading booking requests:', error);
      toast.error('Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  };

  const loadRequestDetails = async (requestId: string) => {
    try {
      console.log('🔍 [RequestDetails] Loading details for requestId:', requestId);
      if (!requestId) {
        console.error('❌ [RequestDetails] No requestId provided');
        toast.error('Invalid request ID');
        return;
      }
      
      const details = await getBookingRequestById(requestId);
      console.log('✅ [RequestDetails] Details loaded successfully:', details);
      setDetailsDialog({ open: true, request: details });
    } catch (error) {
      console.error('❌ [RequestDetails] Error loading request details:', error);
      toast.error('Failed to load request details');
    }
  };

  const handleResponseSubmit = async () => {
    if (!responseDialog.request) return;

    try {
      // Prepare properly typed modifications for the API
      const modifications = responseDialog.type === 'modify' ? {
        dateRange: responseForm.proposedModifications.dateRange.startDate && responseForm.proposedModifications.dateRange.endDate ? {
          startDate: new Date(responseForm.proposedModifications.dateRange.startDate),
          endDate: new Date(responseForm.proposedModifications.dateRange.endDate)
        } : undefined,
        pricing: responseForm.proposedModifications.pricing.modifiedPrice ? {
          modifiedPrice: parseFloat(responseForm.proposedModifications.pricing.modifiedPrice) || 0,
          reason: responseForm.proposedModifications.pricing.reason
        } : undefined,
        options: responseForm.proposedModifications.options
      } : undefined;

      await respondToBookingRequest(
        responseDialog.request.id,
        {
          response: responseDialog.type,
          message: responseForm.message,
          proposedModifications: modifications
        }
      );

      toast.success(`Request ${responseDialog.type}ed successfully!`);
      setResponseDialog({ open: false, type: 'accept', request: null });
      setResponseForm({
        message: '',
        proposedModifications: {
          dateRange: { startDate: '', endDate: '' },
          pricing: { modifiedPrice: '', reason: '' },
          options: {}
        }
      });
      loadBookingRequests();
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error('Failed to respond to request');
    }
  };

  const openResponseDialog = (type: 'accept' | 'decline' | 'modify', request: BookingRequestListItem) => {
    setResponseDialog({ open: true, type, request });
    setResponseForm({
      message: type === 'accept' 
        ? `Great news! We'd love to host your ${request.customAdventureName} adventure. We'll be in touch soon to finalize the details.`
        : type === 'decline'
        ? `Thank you for your interest in our ${request.customAdventureName} adventure. Unfortunately, we're unable to accommodate your request for the selected dates.`
        : `Thank you for your interest! We'd like to propose some modifications to make this adventure perfect for you.`,
      proposedModifications: {
        dateRange: { 
          startDate: format(new Date(request.requestedDateRange.startDate), 'yyyy-MM-dd'),
          endDate: format(new Date(request.requestedDateRange.endDate), 'yyyy-MM-dd')
        },
        pricing: { 
          modifiedPrice: request.estimatedPrice.totalEstimate.toString(), 
          reason: '' 
        },
        options: {}
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const config = {
      pending: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
      under_review: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Eye },
      accepted: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      declined: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      modified: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Edit3 },
      converted_to_booking: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Heart },
      cancelled: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle }
    }[status] || { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertTriangle };

    const Icon = config.icon;
    return (
      <Badge className={cn("px-3 py-1 font-medium border", config.color)}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const getRequestStats = () => {
    return {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending' || r.status === 'under_review').length,
      responded: requests.filter(r => ['accepted', 'declined', 'modified'].includes(r.status)).length,
      converted: requests.filter(r => r.status === 'converted_to_booking').length
    };
  };

  // Filter requests based on active tab
  const getFilteredRequests = () => {
    if (activeTab === 'all') return requests;
    
    return requests.filter(request => {
      if (activeTab === 'pending') return request.status === 'pending' || request.status === 'under_review';
      if (activeTab === 'responded') return ['accepted', 'declined', 'modified'].includes(request.status);
      if (activeTab === 'converted') return request.status === 'converted_to_booking';
      return request.status === activeTab;
    });
  };

  // Define table columns
  const columns: TableColumn<BookingRequestListItem>[] = [
    {
      key: 'requestNumber',
      header: 'Request #',
      accessor: (item) => (
        <div className="font-mono text-sm font-medium">
          {item.requestNumber}
        </div>
      ),
      sortable: true,
      width: '120px'
    },
    {
      key: 'customAdventureName',
      header: 'Adventure',
      accessor: (item) => (
        <div className="font-medium text-gray-900">
          {item.customAdventureName}
        </div>
      ),
      sortable: true
    },
    {
      key: 'customerName',
      header: 'Customer',
      accessor: (item) => (
        <div>
          <div className="font-medium text-gray-900">
            {item.customerName || 'Unknown Customer'}
          </div>
          <div className="text-sm text-gray-500">
            {item.customerEmail || 'No email provided'}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'dateRange',
      header: 'Date Range',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <div className="text-sm">
            <div className="font-medium">
              {format(new Date(item.requestedDateRange.startDate), 'MMM d')} - {format(new Date(item.requestedDateRange.endDate), 'MMM d, yyyy')}
            </div>
            <div className="text-gray-500 capitalize">
              {item.requestedDateRange.flexibility.replace('_', ' ')}
            </div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'participants',
      header: 'Participants',
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <div className="text-sm">
            <div className="font-medium">{item.participants.totalCount}</div>
            <div className="text-gray-500">
              {item.participants.adults}A, {item.participants.children}C, {item.participants.students}S
            </div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'estimatedPrice',
      header: 'Estimate',
      accessor: (item) => (
        <div className="text-right">
          <div className="font-bold text-gray-900">
            {formatCurrency(item.estimatedPrice.totalEstimate, item.estimatedPrice.currency)}
          </div>
          <div className="text-sm text-gray-500">{item.estimatedPrice.currency}</div>
        </div>
      ),
      sortable: true,
      className: 'text-right'
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (item) => getStatusBadge(item.status),
      sortable: true,
      width: '140px'
    },
    {
      key: 'createdAt',
      header: 'Created',
      accessor: (item) => (
        <div className="text-sm text-gray-600">
          {format(new Date(item.createdAt), 'MMM d, yyyy')}
        </div>
      ),
      sortable: true,
      width: '100px'
    }
  ];

  // Define table actions
  const actions: TableAction<BookingRequestListItem>[] = [
    {
      key: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (item) => loadRequestDetails(item.id)
    },
    {
      key: 'accept',
      label: 'Accept',
      icon: CheckCircle,
      variant: 'default',
      condition: (item) => item.status === 'pending' || item.status === 'under_review',
      onClick: (item) => openResponseDialog('accept', item)
    },
    {
      key: 'modify',
      label: 'Modify',
      icon: Edit3,
      variant: 'outline',
      condition: (item) => item.status === 'pending' || item.status === 'under_review',
      onClick: (item) => openResponseDialog('modify', item)
    },
    {
      key: 'decline',
      label: 'Decline',
      icon: XCircle,
      variant: 'destructive',
      condition: (item) => item.status === 'pending' || item.status === 'under_review',
      onClick: (item) => openResponseDialog('decline', item)
    }
  ];

  const stats = getRequestStats();
  const filteredRequests = getFilteredRequests();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Adventure Requests</h1>
          <p className="text-base mt-2 text-slate-600">
            Manage custom adventure booking requests from customers
          </p>
        </div>
        <Button onClick={loadBookingRequests} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={getCardClasses('basic')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-gray-600">Total Requests</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={getCardClasses('basic')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.pending}</div>
                <div className="text-sm text-gray-600">Pending Review</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={getCardClasses('basic')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.responded}</div>
                <div className="text-sm text-gray-600">Responded</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={getCardClasses('basic')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Heart className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.converted}</div>
                <div className="text-sm text-gray-600">Converted</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Tabs and Data Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
          <TabsTrigger value="responded">Responded ({stats.responded})</TabsTrigger>
          <TabsTrigger value="converted">Converted ({stats.converted})</TabsTrigger>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <DataTable
            data={filteredRequests}
            columns={columns}
            actions={actions}
            loading={loading}
            onRefresh={loadBookingRequests}
            searchable={true}
            searchPlaceholder="Search by request number, customer, or adventure..."
            searchKeys={['requestNumber', 'customerName', 'customerEmail', 'customAdventureName']}
            filterable={true}
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: [
                  { value: 'pending', label: 'Pending' },
                  { value: 'under_review', label: 'Under Review' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'declined', label: 'Declined' },
                  { value: 'modified', label: 'Modified' },
                  { value: 'converted_to_booking', label: 'Converted' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]
              }
            ]}
            emptyMessage={
              activeTab === 'all' 
                ? "No custom adventure requests yet."
                : `No ${activeTab} requests found.`
            }
            emptyIcon={MessageSquare}
            onRowClick={(item) => loadRequestDetails(item.id)}
          />
        </TabsContent>
      </Tabs>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onOpenChange={(open) => setResponseDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {responseDialog.type === 'accept' && 'Accept Adventure Request'}
              {responseDialog.type === 'decline' && 'Decline Adventure Request'}
              {responseDialog.type === 'modify' && 'Propose Modifications'}
            </DialogTitle>
            <DialogDescription>
              {responseDialog.type === 'accept' && 'Confirm acceptance of this custom adventure booking request. The customer will be notified via email.'}
              {responseDialog.type === 'decline' && 'Decline this custom adventure booking request with an optional message to the customer.'}
              {responseDialog.type === 'modify' && 'Propose modifications to the booking request including dates, pricing, or other options.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Message to Customer
              </label>
              <Textarea
                value={responseForm.message}
                onChange={(e) => setResponseForm(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                placeholder="Write a personalized message..."
              />
            </div>

            {responseDialog.type === 'modify' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Start Date</label>
                    <input
                      type="date"
                      value={responseForm.proposedModifications.dateRange.startDate}
                      onChange={(e) => setResponseForm(prev => ({
                        ...prev,
                        proposedModifications: {
                          ...prev.proposedModifications,
                          dateRange: { ...prev.proposedModifications.dateRange, startDate: e.target.value }
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">End Date</label>
                    <input
                      type="date"
                      value={responseForm.proposedModifications.dateRange.endDate}
                      onChange={(e) => setResponseForm(prev => ({
                        ...prev,
                        proposedModifications: {
                          ...prev.proposedModifications,
                          dateRange: { ...prev.proposedModifications.dateRange, endDate: e.target.value }
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Modified Price</label>
                  <input
                    type="number"
                    value={responseForm.proposedModifications.pricing.modifiedPrice}
                    onChange={(e) => setResponseForm(prev => ({
                      ...prev,
                      proposedModifications: {
                        ...prev.proposedModifications,
                        pricing: { ...prev.proposedModifications.pricing, modifiedPrice: e.target.value }
                      }
                    }))}
                    placeholder="New price amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setResponseDialog({ open: false, type: 'accept', request: null })}
              >
                Cancel
              </Button>
              <Button onClick={handleResponseSubmit}>
                {responseDialog.type === 'accept' && 'Accept Request'}
                {responseDialog.type === 'decline' && 'Decline Request'}
                {responseDialog.type === 'modify' && 'Send Modifications'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => setDetailsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              View complete information about this custom adventure booking request including customer details, selected options, and communication history.
            </DialogDescription>
          </DialogHeader>
          {detailsDialog.request && (
            <div className="space-y-6">
              {/* Basic Information */}
              <Card className={getCardClasses('basic')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-600">Adventure Name</label>
                      <p className="text-base font-semibold">{detailsDialog.request.customAdventureName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Request Number</label>
                      <p className="text-base font-mono">{detailsDialog.request.requestNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Status</label>
                      <div className="mt-1">{getStatusBadge(detailsDialog.request.status)}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Created</label>
                      <p className="text-base">{format(new Date(detailsDialog.request.createdAt), 'PPP p')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Information */}
              <Card className={getCardClasses('basic')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-600">Name</label>
                      <p className="text-base font-semibold">{detailsDialog.request.customerName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Email</label>
                      <p className="text-base">{detailsDialog.request.customerEmail}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Phone</label>
                      <p className="text-base">{detailsDialog.request.customerDetails?.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Preferred Contact</label>
                      <p className="text-base capitalize">{detailsDialog.request.customerDetails?.preferredContactMethod || 'Email'}</p>
                    </div>
                  </div>
                  {detailsDialog.request.customerDetails?.specialRequests && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-slate-600">Special Requests</label>
                      <p className="text-base mt-1 p-3 bg-slate-50 rounded-lg">{detailsDialog.request.customerDetails.specialRequests}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Trip Details */}
              <Card className={getCardClasses('basic')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Trip Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-slate-600">Date Range</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-base">
                          {format(new Date(detailsDialog.request.requestedDateRange.startDate), 'MMM d, yyyy')} - {format(new Date(detailsDialog.request.requestedDateRange.endDate), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Flexibility</label>
                      <p className="text-base capitalize mt-1">{detailsDialog.request.requestedDateRange.flexibility.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Participants</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-base font-medium">{detailsDialog.request.participants.totalCount} total</span>
                        <span className="text-sm text-slate-500">
                          ({detailsDialog.request.participants.adults}A, {detailsDialog.request.participants.children}C, {detailsDialog.request.participants.students}S)
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-600">Total Estimated Price</label>
                      <div className="flex items-center gap-2 mt-1">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="text-lg font-bold text-primary">{formatCurrency(detailsDialog.request.estimatedPrice.totalEstimate, detailsDialog.request.estimatedPrice.currency)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Price Breakdown - Separate Card */}
              {detailsDialog.request?.priceBreakdown && detailsDialog.request.priceBreakdown.length > 0 && detailsDialog.request && (() => {
                const request = detailsDialog.request;
                if (!request.priceBreakdown) return null;
                const breakdown = request.priceBreakdown;
                const baseItems = breakdown.filter(item => 
                  item.label === request.customAdventureName || item.label.toLowerCase().includes('base')
                );
                const extraItems = breakdown.filter(item => 
                  item.label !== request.customAdventureName && !item.label.toLowerCase().includes('base')
                );
                
                return (
                  <Card className={getCardClasses('basic')}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-primary" />
                        Price Breakdown
                      </h3>
                      <div className="space-y-4">
                        {/* Base Price Section */}
                        {baseItems.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wide">Base Price</h4>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                              {baseItems.map((item, idx) => (
                                <div key={idx} className="pb-2 last:pb-0">
                                  <div className="flex justify-between items-start mb-1">
                                    <span className="font-semibold text-slate-900 flex-1">{item.label}</span>
                                    <span className="font-bold text-primary ml-3 flex-shrink-0 text-lg">
                                      {formatCurrency(item.amount, request.estimatedPrice.currency)}
                                    </span>
                                  </div>
                                  {item.description && (
                                    <div className="text-xs text-blue-600 font-mono mt-1">
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Extras/Add-ons Section */}
                        {extraItems.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wide">Add-ons & Extras</h4>
                            <div className="space-y-2">
                              {extraItems.map((item, idx) => (
                                <div key={idx} className="border-b border-slate-100 last:border-b-0 pb-2 last:pb-0">
                                  <div className="flex justify-between items-start mb-1">
                                    <span className="font-medium text-slate-700 flex-1">{item.label}</span>
                                    <span className="font-semibold text-slate-900 ml-3 flex-shrink-0">
                                      {formatCurrency(item.amount, request.estimatedPrice.currency)}
                                    </span>
                                  </div>
                                  {item.description && (
                                    <div className="text-xs text-blue-600 font-mono mt-0.5">
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Additional Options - Only show options NOT in price breakdown */}
              {detailsDialog.request.selectedOptions && Object.keys(detailsDialog.request.selectedOptions).length > 0 && (() => {
                // Get breakdown labels to filter out duplicates
                const breakdownLabels = detailsDialog.request.priceBreakdown?.map(item => item.label.toLowerCase()) || [];
                // Filter out options that are already shown in breakdown and people/days fields
                const filteredOptions = Object.entries(detailsDialog.request.selectedOptions).filter(([key, value]) => {
                  const keyLower = key.toLowerCase();
                  // Skip if already in breakdown
                  if (breakdownLabels.some(label => label.includes(keyLower) || keyLower.includes(label))) return false;
                  // Skip people/days fields (shown in Trip Details)
                  if (keyLower.includes('people') || keyLower.includes('person') || keyLower.includes('day') || keyLower.includes('days')) return false;
                  // Skip boolean true values for options that might be in breakdown
                  if (value === true && breakdownLabels.some(label => keyLower.includes(label.replace(/\s+/g, '')))) return false;
                  return true;
                });
                
                return filteredOptions.length > 0 ? (
                  <Card className={getCardClasses('basic')}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-4">Additional Preferences</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredOptions.map(([key, value], index) => (
                          <div key={`option-${key}-${index}`} className="flex justify-between items-start py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm font-medium text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <span className="text-sm text-slate-900 ml-3 text-right flex-shrink-0 max-w-[60%]">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              {/* Business Response */}
              {detailsDialog.request.businessResponse && (
                <Card className={getCardClasses('basic')}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Business Response</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-600">Response</label>
                        <p className="text-base font-semibold capitalize">{detailsDialog.request.businessResponse.response}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-600">Responded At</label>
                        <p className="text-base">{format(new Date(detailsDialog.request.businessResponse.respondedAt), 'PPP p')}</p>
                      </div>
                    </div>
                    {detailsDialog.request.businessResponse.message && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-slate-600">Message</label>
                        <p className="text-base mt-1 p-3 bg-slate-50 rounded-lg">{detailsDialog.request.businessResponse.message}</p>
                      </div>
                    )}
                    {detailsDialog.request.businessResponse.proposedModifications && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-slate-600">Proposed Modifications</label>
                        <div className="mt-1 p-3 bg-slate-50 rounded-lg space-y-2">
                          {detailsDialog.request.businessResponse.proposedModifications.dateRange && (
                            <p><strong>New Dates:</strong> {format(new Date(detailsDialog.request.businessResponse.proposedModifications.dateRange.startDate), 'MMM d, yyyy')} - {format(new Date(detailsDialog.request.businessResponse.proposedModifications.dateRange.endDate), 'MMM d, yyyy')}</p>
                          )}
                          {detailsDialog.request.businessResponse.proposedModifications.pricing && (
                            <p><strong>New Price:</strong> {formatCurrency(Number(detailsDialog.request.businessResponse.proposedModifications.pricing.modifiedPrice), detailsDialog.request.estimatedPrice?.currency || 'USD')} - {detailsDialog.request.businessResponse.proposedModifications.pricing.reason}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Communication History */}
              {detailsDialog.request.communication?.messages && detailsDialog.request.communication.messages.length > 0 && (
                <Card className={getCardClasses('basic')}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Communication History</h3>
                    <div className="space-y-3">
                      {detailsDialog.request.communication.messages.map((message, index) => (
                        <div key={`message-${index}-${message.timestamp}-${message.type}`} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                          <MessageSquare className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {message.type}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {message.direction.replace('_', ' ')}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                {format(new Date(message.timestamp), 'MMM d, yyyy p')}
                              </span>
                            </div>
                            <p className="text-sm">{message.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setDetailsDialog({ open: false, request: null })}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 